import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {NotificationService, RoutesService} from '@raven';
import {CurrencyPipe} from '@angular/common';
import {Store} from '@ngrx/store';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PatronSelectors} from '@store/store.selectors';
import {PatronAccountStatus} from '@store/patron/patron.types';

@Component({
  selector: 'rn-account-status-warning',
  templateUrl: './account-status-warning.component.html',
  styleUrl: './account-status-warning.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AccountStatusWarningComponent implements OnInit {
  @Input() context: 'dropdown' | 'notification' | 'banner' | 'placeHold';
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly changeDetectionRef = inject(ChangeDetectorRef);

  notificationOpened: boolean = false;
  statusTitle?: string;
  statusMessage?: string;
  statusActionLabel?: string;
  statusActionLink?: () => unknown;

  constructor(
    public routingService: RoutesService,
    private notificationService: NotificationService,
    private currencyPipe: CurrencyPipe,
  ) {
  }

  ngOnInit() {
    this.store.select(PatronSelectors.AccountStatus.status).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((status) => {
      this.setStatus(status);
      this.openNotification();
    });
  }

  setStatus(act: PatronAccountStatus): void {
    this.statusTitle = 'Account Frozen';
    if (act.minimumBalanceDue > 0) {
      const formattedBalance = this.currencyPipe.transform(act.maximumAccountBalance);
      this.statusMessage = (this.context === 'placeHold')
        ? `There are charges on your account that exceed ${formattedBalance}. In order to place a hold on any items you need to pay down the charges.`
        : `Your account balance is over ${formattedBalance} and your lending privileges have been suspended. Please make a payment to restore your account.`;
      this.statusActionLabel = 'Make a Payment.';
      this.statusActionLink = this.routingService.goToPatronBilling.bind(this.routingService);
    } else if (act.blocks.includes('MAX_ITEMS_OVERDUE')) {
      this.statusMessage = 'The number of overdue items on your account exceeds the library\'s limit and your lending privileges have been suspended. Please return or renew your items to restore your account.';
      this.statusActionLabel = 'Renew Items.';
      this.statusActionLink = this.routingService.goToCheckouts.bind(this.routingService);
    } else if (act.blocks.includes('MAX_ITEMS_OUT')) {
      this.statusMessage = 'You have checked out the maximum number of items and your lending privileges have been paused until some items have been returned.';
    } else if (act.blocks.includes('MAX_LOST')) {
      this.statusMessage = 'The number of damaged or lost items exceeds the library\'s threshold and your lending privileges have been suspended. Please contact the library for assistance.';
    } else if (act.blocks.includes('CARD_EXPIRED')) {
      this.statusMessage = 'Your library card has expired, you may be able to renew it.';
      this.statusActionLabel = 'View library card';
      this.statusActionLink = this.routingService.goToMyCard.bind(this.routingService);
    } else if (act.blocks.length > 0) {
      this.statusMessage = 'Your account has been frozen, please contact the library for assistance.';
    } else {
      this.statusMessage = undefined;
      this.statusTitle = undefined;
    }
    this.changeDetectionRef.markForCheck();
  }

  openNotification(): void {
    if (this.context === 'notification' && this.statusMessage && !this.notificationOpened) {
      const snackBarRef = this.notificationService.showSnackbarError(this.statusMessage, this.statusActionLabel, 0);
      snackBarRef.onAction().subscribe(() => {
        this.statusActionLink();
        snackBarRef.dismiss();
      });
      this.notificationOpened = true;
    }
  }
}
